import React, { useCallback, useState, useMemo, useRef } from 'react'
import _ from 'lodash'
import { connect } from 'react-redux'
import { withRouter } from 'react-router-dom'
import { Portal } from 'react-portal'
import { isAdmin } from '../../../common/roles'

import api, { apiAction, apiActionWithRoute } from '../../../api'
import useModel from '../../../common/useModel'

import Recipe from './recipe'
import Variations from './variations'
import { SaveButton, ConfirmModal, EditorTransitionPrompt, Button } from  '../../../components'
import { LockIcon } from '../../../components/icons'
import actions from './actions'
import { defaultRecipe }  from '../../../common/recipe'
import { defaultVariation }  from '../../../common/variation'
import { Loading } from  '../../../components'
import S from './styled'

const RecipeView = props => {

  const {
    locales,
    match,
    recipe = defaultRecipe,
    variations = [],
    appliances,
    runCommand,
    userId,
    userName,
    role,
    recipeId,
  } = props

  const [lockStatus, setLockStatus] = useState(_.get(recipe, 'editLock', {
    isLocked: false,
    lockerName: userName,
    lockedBy: userId,
  }))
  const [showLockConfirm, setShowLockConfirm] = useState(false)
  const [loading, setLoading] = useState(false)
  const locked = lockStatus.isLocked && userId !== lockStatus.lockedBy

  const lockUnlock = useCallback(() => {
    (async () => {
      try {
        setShowLockConfirm(false)
        setLoading(true)
        const lockInfo = await runCommand(recipeId, lockStatus.isLocked ? 'unlock' : 'lock')
        setLockStatus(lockInfo)
      } finally {
        setLoading(false)
      }
    })()
  }, [lockStatus.isLocked, recipeId, runCommand])

  const handleLockUnlock = useCallback(() => {
    if (!lockStatus.isLocked) {
      setShowLockConfirm(true)
    } else {
      lockUnlock()
    }
  }, [lockUnlock, lockStatus.isLocked])


  const defaultLocale = (locales[0] || {}).id

  const isNew = match.params.recipeId === 'new'

  const emptyVariation = useMemo(() => defaultVariation(defaultLocale, appliances), [defaultLocale, appliances])

  const initialVariation = useMemo(() =>
    isNew ? [emptyVariation] : variations,
  [isNew, emptyVariation, variations],
  )

  const recipeInitial = useMemo(() => ({
    values: isNew ? defaultRecipe : recipe,
  }),[isNew, recipe])

  const variationsInitial = useMemo(() => ({
    values: initialVariation,
    isDirty: !!isNew,
  }),[initialVariation, isNew])

  const recipeModel = useModel(recipeInitial)

  const variationsModel = useModel(variationsInitial)

  const [resetOpened, setResetOpen] = useState(false)

  const resetDirtyHandler = useCallback(() => {
    recipeModel.resetDirty()
    variationsModel.resetDirty()
  }, [recipeModel, variationsModel ])

  const { onSave, onAdd, saving, onExport, onImport } = actions({ ...props, resetDirty: resetDirtyHandler })

  const handleSave = useCallback(() => {
    (isNew ? onAdd : onSave)(recipeModel, variationsModel)
  }, [isNew, onAdd, onSave, recipeModel, variationsModel])

  const handleReset = useCallback(() => {
    setResetOpen(true)
  }, [])

  const inputEl = useRef(null)
  const formEl = useRef(null)

  return (
    <div>
      {
        locales && locales.length > 0 ? (
          <React.Fragment>
            {
              lockStatus.isLocked && (
                <S.Locked>
                  <LockIcon />&nbsp;Recipe is locked by { userId === lockStatus.lockedBy ? 'you' : lockStatus.lockerName}
                </S.Locked>
              )
            }
            <EditorTransitionPrompt when={recipeModel.isDirty || variationsModel.isDirty}/>
            <Button onClick={() => onExport(recipeModel, variationsModel, locales)}>Export translates</Button>
            <Button onClick={() => inputEl.current.click()} disabled={locked}>
              Import translates
            </Button>
            <form style={{ display: 'none' }} ref={formEl}
            ><input
                type="file"
                name="import"
                onChange={
                  e => e.target.files && e.target.files.length &&
                    onImport(e.target.files[0],recipeModel, variationsModel, formEl)
                }
                accept=".csv"
                ref={inputEl}
              /></form>
            <Recipe
              {...props}
              defaultLocale={defaultLocale}
              model={recipeModel}
              locked={locked}
              handleLockUnlock={handleLockUnlock}
              isNew={isNew}
              unlockDisabled={locked && userId !== lockStatus.lockedBy && !isAdmin(role)}
              lockRealStatus={lockStatus.isLocked}
            />
            <Portal node={document && document.getElementById('main-content')}>
              <Variations
                {...props}
                servings={_.get(recipeModel, 'values.servings', [])}
                model={variationsModel}
                defaultVariation={emptyVariation}
                defaultLocale={defaultLocale}
                locked={locked}
              />
            </Portal>
            <SaveButton
              disabled={
                (!recipeModel.isValid || !variationsModel.isValid) ||
                !(variationsModel.isDirty || recipeModel.isDirty) || locked
              }
              onClick={handleSave}
              canCancel={recipeModel.isDirty || variationsModel.isDirty}
              onCancel={handleReset}
            />
            {
              resetOpened && (
                <ConfirmModal
                  isOpened
                  onClose={() => setResetOpen(false)}
                  onConfirm={() => {
                    recipeModel.reset('values')
                    variationsModel.reset('values')
                    setResetOpen(false)
                  }}
                  redConfirm
                >
                  Are you sure you want to <b>reset</b> fields?
                </ConfirmModal>
              )
            }
            {
              showLockConfirm && <ConfirmModal
                isOpened
                onClose={() => setShowLockConfirm(false)}
                onConfirm={lockUnlock}
                redConfirm
              >
                Are you sure you want to <b>lock</b> this recipe?<br/><br/><br/>
                <i> It will be impossible for others to edit it.</i>
              </ConfirmModal>
            }
          </React.Fragment>
        ) : <div>Please create at least one locale, region and food category first</div>
      }
      {
        (saving || loading) && <Loading overlay/>
      }
    </div>
  )
}

const mapStateToProps = (state, { entityName }) => ({
  foodKinds: state.foodKinds.list,
  tags: state.tags.list,
  locales: state.locales.list,
  recipe: state.recipes.item,
  variations: state.recipes.variations,
  appliances: state.appliances.list,
  libraryTexts: state.libraryTexts.list,
  ingredients: state.recipes.ingredients,
  cooking: state.labellevels.cooking,
  browning: state.labellevels.browning,
  units: state.units.list,
  role: state.session.role,
  userId: state.session.userId,
  userName: state.session.userName,
  regions: state.regions.list,
})

const mapDispatchToProps = (dispatch, { entityName } ) => ({
  getRecipe: entityId => dispatch(apiActionWithRoute(api.getRecipe)(entityId)()),
  addRecipe: item => dispatch(apiAction(api.addRecipe)(item)),
  deleteRecipe: entityId => dispatch(apiActionWithRoute(api.deleteRecipe)(entityId)()),
  updateRecipe: (entityId, item) => dispatch(apiActionWithRoute(api.updateRecipe)(entityId)(item)),
  addVariant: (entityId, variant) => dispatch(apiActionWithRoute(api.addRecipeVariant)(
    `${entityId}/recipe`,
  )(variant)),
  deleteVariant: (entityId, variantId) => dispatch(apiActionWithRoute(api.deleteRecipeVariant)(
    `${entityId}/recipe/${variantId}`,
  )()),
  updateVariant: (entityId, variantId, variant) => dispatch(apiActionWithRoute(api.updateRecipeVariant)(
    `${entityId}/recipe/${variantId}`,
  )(variant)),
  runCommand: (entityId, command) => dispatch(apiActionWithRoute(api.recipeCommand)(
    `${entityId}/command`,
  )({ command })),
})

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(RecipeView))
